
import { Variation } from "@/components/admin/locations/types/variation";
import { addDays, format, isSameDay } from "date-fns";

/**
 * Calculates the end date based on the number of days in a variation
 * @param startDate The start date of the session
 * @param numDays The number of days for the session
 * @returns The calculated end date
 */
export const calculateEndDateFromNumDays = (startDate: Date, numDays: number = 1): Date => {
  // Calculate end date by adding numDays - 1 days to the start date
  // (we subtract 1 because the start date counts as day 1)
  return addDays(startDate, Math.max(0, numDays - 1));
};

/**
 * Gets the default end date for a session based on the selected variation
 * @param startDate The start date of the session
 * @param variationId The ID of the selected variation
 * @param variations The list of available variations
 * @returns The calculated end date
 */
export const getDefaultEndDate = (
  startDate: Date,
  variationId: string,
  variations: Variation[]
): Date => {
  // Find the selected variation
  const selectedVariation = variations.find(v => v.key === variationId);
  
  // Get the number of days from the variation, default to 1 if not specified
  const numDays = selectedVariation?.numDays || 1;
  
  // Calculate the end date
  return calculateEndDateFromNumDays(startDate, numDays);
};

/**
 * Format a date for display in title format (e.g., "Jul 15")
 * @param date Date to format
 * @returns Formatted date string for title display
 */
export const formatDateForTitle = (date: Date): string => {
  return format(date, 'MMM d');
};

/**
 * Check if two dates fall on the same day
 * @param date1 First date to compare
 * @param date2 Second date to compare
 * @returns True if both dates are on the same day
 */
export const areDatesOnSameDay = (date1: Date, date2: Date): boolean => {
  return isSameDay(date1, date2);
};
